/**
 * Client-side revalidation result types.
 *
 * These types represent the outcome of diffing baked cart prices against the
 * current pricing facts returned by POST /v1/cart/revalidate. The diff
 * computation lives in client/lib/cart/cart-revalidate.ts.
 *
 * Source: 2026-06-20-wandero-cart-price-revalidation spec.
 */

import { z } from 'zod';

// 'stale' is the age-based dimension (2026-06-20-wandero-cart-ttl-expiry): a line
// older than CART_TTL_MS, surfaced independently of any price drift. Additive —
// existing price/availability statuses are unchanged.
export const CART_ITEM_REVAL_STATUS = ['unchanged', 'price-changed', 'unavailable', 'stale'] as const;
export const CartItemRevalStatus = z.enum(CART_ITEM_REVAL_STATUS);
export type CartItemRevalStatus = z.infer<typeof CartItemRevalStatus>;

/** Per-line outcome after diffing baked price vs current facts. */
export const CartItemRevalidationSchema = z.object({
  item_id: z.string().uuid(),
  status: CartItemRevalStatus,
  // Baked-at-add price (string) — what the cart currently shows.
  old_price_total: z.string(),
  // Current recomputed price (string); null when unavailable / no current price.
  new_price_total: z.string().nullable(),
  // Signed direction for non-alarming copy ('up' | 'down' | null when unchanged/unavailable).
  direction: z.enum(['up', 'down']).nullable(),
});
export type CartItemRevalidation = z.infer<typeof CartItemRevalidationSchema>;

/** Aggregate summary the notice consumes. */
export const CartRevalidationResultSchema = z.object({
  // false ⇒ notice renders nothing (all lines unchanged / no result).
  hasChanges: z.boolean(),
  items: z.array(CartItemRevalidationSchema),
  counts: z.object({
    unchanged: z.number().int().nonnegative(),
    priceChanged: z.number().int().nonnegative(),
    unavailable: z.number().int().nonnegative(),
    // Lines older than the cart TTL (2026-06-20-wandero-cart-ttl-expiry).
    stale: z.number().int().nonnegative(),
  }),
});
export type CartRevalidationResult = z.infer<typeof CartRevalidationResultSchema>;
